import {FeatureName} from '@contentlab/types/project';
import translate from '@/utils/translate';

export interface FeatureState {
  disabled: boolean;
  tooltip?: string;
}

/**
 * LaunchDarkly flags interface for ContentLab features
 */
export interface ContentLabFlags {
  underMaintenanceFeatures?: string[]; // JSON array of feature names under maintenance
  clCaptions?: boolean;
  clAiAvatar?: boolean;
  clUpscale?: boolean;
}

/**
 * Gets the state of a feature including whether it's disabled and tooltip text.
 *
 * @param feature - The feature name to check
 * @param flags - LaunchDarkly flags object
 * @returns Object with disabled state and tooltip if disabled
 *
 * @example
 * // LaunchDarkly flags: { underMaintenanceFeatures: ["rephrasing", "ai_dubbing"] }
 * getFeatureState(FeatureName.REPHRASING, flags) // returns { disabled: true, tooltip: "This feature is under maintenance" }
 * getFeatureState(FeatureName.REFORMATTING, flags) // returns { disabled: false }
 */
export function getFeatureState(
  feature: FeatureName,
  flags: ContentLabFlags
): FeatureState {
  const disabled = isFeatureDisabled(feature, flags);

  return {
    disabled,
    tooltip: disabled
      ? translate('This feature is under maintenance')
      : undefined,
  };
}

/**
 * Checks if a feature is disabled based on LaunchDarkly feature flags.
 *
 * @param feature - The feature name to check
 * @param flags - LaunchDarkly flags object
 * @returns true if the feature is disabled, false otherwise
 *
 * @example
 * // LaunchDarkly flags: { underMaintenanceFeatures: ["rephrasing", "ai_dubbing"] }
 * isFeatureDisabled(FeatureName.REPHRASING, flags) // returns true
 * isFeatureDisabled(FeatureName.REFORMATTING, flags) // returns false
 */
export function isFeatureDisabled(
  feature: FeatureName,
  flags: ContentLabFlags
): boolean {
  const {underMaintenanceFeatures} = flags;

  if (!underMaintenanceFeatures || !Array.isArray(underMaintenanceFeatures)) {
    return false;
  }

  // Check if feature is in the maintenance array (case-insensitive)
  return underMaintenanceFeatures
    .map((f) => f.toLowerCase())
    .includes(feature.toLowerCase());
}

/**
 * Gets the list of disabled features from LaunchDarkly flags.
 *
 * @param flags - LaunchDarkly flags object
 * @returns Array of disabled feature names, empty array if none disabled
 *
 * @example
 * // LaunchDarkly flags: { underMaintenanceFeatures: ["rephrasing", "ai_dubbing"] }
 * getDisabledFeatures(flags) // returns [FeatureName.REPHRASING, FeatureName.AI_DUBBING]
 */
export function getDisabledFeatures(flags: ContentLabFlags): FeatureName[] {
  return Object.values(FeatureName).filter((feature) =>
    isFeatureDisabled(feature, flags)
  );
}

/**
 * Checks if a feature is available to the current user.
 * Combines feature-specific rollout flags and maintenance status.
 *
 * @param feature - The feature name to check
 * @param flags - LaunchDarkly flags object
 * @returns true if feature is available, false otherwise
 *
 * @example
 * isFeatureAvailable(FeatureName.UPSCALE, flags) // false if not rolled out or in maintenance
 * isFeatureAvailable(FeatureName.REFORMATTING, flags) // true (no specific flag required)
 */
export function isFeatureAvailableToUser(
  feature: FeatureName,
  flags: ContentLabFlags
): boolean {
  if (feature === FeatureName.CAPTIONS && !flags.clCaptions) {
    return false;
  }
  if (feature === FeatureName.AI_AVATAR && !flags.clAiAvatar) {
    return false;
  }
  if (feature === FeatureName.UPSCALE && !flags.clUpscale) {
    return false;
  }

  if (isFeatureDisabled(feature, flags)) {
    return false;
  }

  return true;
}

/**
 * Gets list of all available features for the current user.
 *
 * @param flags - LaunchDarkly flags object
 * @returns Array of available feature names
 *
 * @example
 * getAvailableFeatures(flags) // returns [FeatureName.REFORMATTING, FeatureName.AI_DUBBING, ...]
 */
export function getAvailableFeatures(flags: ContentLabFlags): FeatureName[] {
  return Object.values(FeatureName).filter((feature) =>
    isFeatureAvailableToUser(feature, flags)
  );
}
